import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Divider,
  Empty,
  Image,
  Input,
  Popconfirm,
  Progress,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { ScheduleToggle } from '@/components/ScheduleToggle';
import {
  videoApi,
  type FacelessVideo,
  type PublishPlatform,
  type VideoStatus,
  type VideoVisualMode,
} from '../video.api';
import { CostTag } from '../../ai/ui/CostTag';
import { ChannelKitCard } from './ChannelKitCard';

/** URL-safe slug for download filenames. */
const slug = (s: string): string =>
  (s || 'video')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'video';

/** Adds ?download=<name> so the file streams as a save-as with a friendly name. */
const downloadUrl = (url: string, name: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}download=${encodeURIComponent(name)}`;

const STATUS_COLOR: Record<VideoStatus, string> = {
  queued: 'default',
  scripting: 'blue',
  generating_media: 'blue',
  assembling: 'gold',
  ready: 'green',
  failed: 'red',
};
const STATUS_LABEL: Record<VideoStatus, string> = {
  queued: 'Queued',
  scripting: 'Writing script',
  generating_media: 'Generating scenes & narration',
  assembling: 'Assembling video',
  ready: 'Ready',
  failed: 'Failed',
};
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'ar', label: 'Arabic' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
];
const LENGTHS = [
  { value: 60, label: '1 minute' },
  { value: 120, label: '2 minutes' },
  { value: 180, label: '3 minutes' },
  { value: 300, label: '5 minutes' },
];

const IN_PROGRESS: VideoStatus[] = ['queued', 'scripting', 'generating_media', 'assembling'];

/** A ready video still missing an auto-routed destination. */
const hasPendingDestinations = (v: FacelessVideo): boolean => {
  if (v.status !== 'ready') return false;
  const fullPending = (['youtube', 'meta'] as PublishPlatform[]).some(
    (p) => !v.published.some((r) => r.platform === p),
  );
  const partsPending = v.parts.some((part) =>
    (['tiktok', 'youtube'] as PublishPlatform[]).some(
      (p) => !part.published.some((r) => r.platform === p),
    ),
  );
  return fullPending || partsPending;
};

/** True while a scheduled drip is still expected to be rolling out. */
const isDripping = (v: FacelessVideo): boolean =>
  !!v.publishPendingUntil &&
  new Date(v.publishPendingUntil).getTime() > Date.now() &&
  hasPendingDestinations(v);

export function VideoTab({
  orgId,
  projectId,
  canEdit,
  lockedVisualMode,
}: {
  orgId: string;
  projectId: string;
  canEdit: boolean;
  /**
   * Pins the visual mode and hides the selector. A did-you-know project IS the
   * background-clip format, so offering to switch it to generated scenes would
   * only be a way to break it.
   */
  lockedVisualMode?: VideoVisualMode;
}) {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const key = ['videos', orgId, projectId] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: () => videoApi.list(orgId, projectId),
    refetchInterval: (q) => {
      const data = q.state.data ?? [];
      // Fast poll while anything is still rendering.
      if (data.some((v) => IN_PROGRESS.includes(v.status))) return 4000;
      // Slower poll while a scheduled drip is still rolling out, so ✓s appear.
      if (data.some(isDripping)) return 15000;
      return false;
    },
  });

  // Channel autopilot: set a theme once, the agent keeps ideating & generating.
  const channelKey = ['video-channel', orgId, projectId] as const;
  const channelQuery = useQuery({
    queryKey: channelKey,
    queryFn: () => videoApi.getChannel(orgId, projectId),
  });
  const [theme, setTheme] = useState('');
  const [chLanguage, setChLanguage] = useState('en');
  const [chLength, setChLength] = useState(300);
  const [perRun, setPerRun] = useState(1);
  const [visualMode, setVisualMode] = useState<VideoVisualMode>('generated');
  const [backgroundUrl, setBackgroundUrl] = useState('');
  const [autoPublish, setAutoPublish] = useState(false);
  const [publishTime, setPublishTime] = useState('18:00');
  useEffect(() => {
    const d = channelQuery.data;
    if (d) {
      setTheme(d.theme);
      setChLanguage(d.language);
      setChLength(d.targetSeconds);
      setPerRun(d.videosPerRun);
      setVisualMode(lockedVisualMode ?? d.visualMode);
      setBackgroundUrl(d.backgroundUrl);
      setAutoPublish(d.autoPublish);
      setPublishTime(d.publishTime);
    }
  }, [channelQuery.data, lockedVisualMode]);
  // A stock-loop channel can't render anything without a clip, so the save and
  // autopilot buttons stay disabled until there is one.
  const channelIncomplete =
    theme.trim().length < 3 || (visualMode === 'stock_loop' && !backgroundUrl.trim());

  const saveChannel = useMutation({
    mutationFn: () =>
      videoApi.updateChannel(orgId, projectId, {
        theme,
        language: chLanguage,
        targetSeconds: chLength,
        videosPerRun: perRun,
        visualMode,
        backgroundUrl: backgroundUrl.trim(),
        autoPublish,
        publishTime,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: channelKey });
      message.success('Channel saved');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to save channel'),
  });
  // Background clips are uploaded, not linked: a pasted URL only works if it
  // serves raw video bytes, and the obvious thing to paste — a YouTube page —
  // serves HTML, which only failed once it reached ffmpeg.
  const [bgPct, setBgPct] = useState<number | undefined>(undefined);
  const uploadBackground = useMutation({
    mutationFn: (file: File) =>
      videoApi.uploadBackground(orgId, projectId, file, (pct) => setBgPct(pct)),
    onSuccess: (channel) => {
      setBgPct(undefined);
      setBackgroundUrl(channel.backgroundUrl);
      void queryClient.invalidateQueries({ queryKey: channelKey });
      message.success('Background clip uploaded');
    },
    onError: (e) => {
      setBgPct(undefined);
      message.error(e instanceof Error ? e.message : 'Upload failed');
    },
  });

  const autopilot = useMutation({
    mutationFn: () => videoApi.runAutopilot(orgId, projectId),
    onSuccess: () => {
      message.success('Autopilot queued — new videos will appear shortly');
      setTimeout(() => void queryClient.invalidateQueries({ queryKey: key }), 3000);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to run autopilot'),
  });

  const [topic, setTopic] = useState('');
  const [language, setLanguage] = useState('en');
  const [targetSeconds, setTargetSeconds] = useState(300);
  // Spacing between posts when publishing everything (0 = all at once).
  const [pubInterval, setPubInterval] = useState(0);

  const create = useMutation({
    mutationFn: () => videoApi.create(orgId, projectId, { topic, language, targetSeconds }),
    onSuccess: () => {
      setTopic('');
      void queryClient.invalidateQueries({ queryKey: key });
      message.success('Video queued — it will render in the background');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to queue video'),
  });

  const publish = useMutation({
    mutationFn: ({
      id,
      platform,
      partIndex,
    }: {
      id: string;
      platform: PublishPlatform;
      partIndex?: number;
    }) => videoApi.publish(orgId, projectId, id, platform, partIndex),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: key });
      message.success(`Published to ${vars.platform}`);
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Publish failed'),
  });

  const publishAll = useMutation({
    mutationFn: ({ id, intervalMinutes }: { id: string; intervalMinutes: number }) =>
      videoApi.publishAll(orgId, projectId, id, intervalMinutes),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: key });
      if (data.scheduled) {
        if (data.count === 0) {
          message.info('Everything is already published');
        } else {
          const last = data.lastAt ? new Date(data.lastAt).toLocaleTimeString() : '';
          message.success(
            `Scheduled ${String(data.count)} posts, one every ${String(data.intervalMinutes)} min` +
              (last ? ` (last around ${last})` : ''),
          );
        }
        return;
      }
      const ok = data.results.filter((r) => r.ok).length;
      const failed = data.results.filter((r) => !r.ok);
      if (data.results.length === 0) {
        message.info('Everything is already published');
      } else if (failed.length === 0) {
        message.success(`Published to ${String(ok)} destination${ok === 1 ? '' : 's'}`);
      } else {
        message.warning(
          `Published ${String(ok)}, ${String(failed.length)} failed: ${failed
            .map((r) => `${r.target} (${r.error ?? 'error'})`)
            .join('; ')}`,
        );
      }
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Publish failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => videoApi.remove(orgId, projectId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      message.success('Video deleted');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to delete'),
  });

  const retry = useMutation({
    mutationFn: (id: string) => videoApi.retry(orgId, projectId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      message.success('Retrying — the video will resume rendering');
    },
    onError: (e) => message.error(e instanceof Error ? e.message : 'Failed to retry'),
  });

  const videos = query.data ?? [];
  // Auto-routed by format: the full video goes to YouTube (regular) + Facebook;
  // short-form parts go to TikTok + YouTube Shorts.
  const FULL_PLATFORMS: { value: PublishPlatform; label: string }[] = [
    { value: 'youtube', label: 'YouTube' },
    { value: 'meta', label: 'Facebook' },
  ];
  const PART_PLATFORMS: { value: PublishPlatform; label: string }[] = [
    { value: 'tiktok', label: 'TikTok' },
    { value: 'youtube', label: 'Shorts' },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ChannelKitCard orgId={orgId} projectId={projectId} canEdit={canEdit} />
      {canEdit ? (
        <Card size="small" title="Channel autopilot">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Input.TextArea
              rows={2}
              placeholder="Channel theme, e.g. Classic novels & drama summarized in 5 minutes (Pride & Prejudice, Romeo & Juliet, Pygmalion…)"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
            />
            <Space wrap>
              <Select
                value={chLanguage}
                onChange={setChLanguage}
                options={LANGUAGES}
                style={{ minWidth: 130 }}
              />
              <Select
                value={chLength}
                onChange={setChLength}
                options={LENGTHS}
                style={{ minWidth: 130 }}
              />
              <Select
                value={perRun}
                onChange={setPerRun}
                style={{ minWidth: 150 }}
                options={[1, 2, 3].map((n) => ({ value: n, label: `${String(n)} video/run` }))}
              />
              {lockedVisualMode ? null : (
                <Select
                  value={visualMode}
                  onChange={setVisualMode}
                  style={{ minWidth: 180 }}
                  options={[
                    { value: 'generated', label: 'Generated scenes' },
                    { value: 'stock_loop', label: 'Background clip' },
                  ]}
                />
              )}
              <Button
                onClick={() => saveChannel.mutate()}
                loading={saveChannel.isPending}
                disabled={channelIncomplete}
              >
                Save channel
              </Button>
              <Button
                type="primary"
                onClick={() => autopilot.mutate()}
                loading={autopilot.isPending}
                disabled={channelIncomplete}
              >
                Generate next now
              </Button>
              <ScheduleToggle
                orgId={orgId}
                projectId={projectId}
                kind="video_autopilot"
                label="Daily autopilot"
                hint="Each day the channel agent ideates fresh on-theme topics and generates new videos automatically."
                canEdit={canEdit}
              />
            </Space>
            {visualMode === 'stock_loop' ? (
              bgPct !== undefined ? (
                <Progress
                  percent={bgPct}
                  status={bgPct === 100 ? 'success' : 'active'}
                  format={() => (bgPct === 100 ? 'Saving…' : `Uploading ${String(bgPct)}%`)}
                />
              ) : (
                <Upload.Dragger
                  accept="video/*"
                  maxCount={1}
                  showUploadList={false}
                  beforeUpload={(file) => {
                    uploadBackground.mutate(file as unknown as File);
                    return false; // raw upload, not antd's
                  }}
                >
                  <p className="ant-upload-text">
                    {backgroundUrl ? 'Replace the background clip' : 'Upload a background clip'}
                  </p>
                  <p className="ant-upload-hint">
                    Gameplay, ASMR, any loop. Every video on this channel narrates over it, starting
                    at a different point.
                  </p>
                </Upload.Dragger>
              )
            ) : null}
            <Space wrap align="center">
              <Switch checked={autoPublish} onChange={setAutoPublish} />
              <Typography.Text>Auto-publish each video when ready</Typography.Text>
              <Typography.Text type="secondary">at</Typography.Text>
              <input
                type="time"
                value={publishTime}
                disabled={!autoPublish}
                onChange={(e) => setPublishTime(e.target.value)}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #d9d9d9' }}
              />
              <Typography.Text type="secondary">Cairo time, daily</Typography.Text>
            </Space>
            <Typography.Text type="secondary">
              Set the theme once — treat your list as seed examples, and the agent will suggest
              fresh adjacent topics without repeating. Or generate a one-off below.{' '}
              {visualMode === 'stock_loop' ? (
                <>
                  <b>Background clip</b> narrates over the clip above instead of generating scene
                  images — no image generation, so these are much cheaper, and each video starts at
                  a different point in the clip.{' '}
                </>
              ) : (
                <>
                  <b>Generated scenes</b> draws an image per beat and animates it.{' '}
                </>
              )}
              With auto-publish on, each finished video is published (full → YouTube + Facebook,
              parts → TikTok + Shorts) at your chosen time — remember to hit <b>Save channel</b>.
            </Typography.Text>
          </Space>
        </Card>
      ) : null}

      {canEdit ? (
        <Card size="small" title="Create a single video">
          <Space direction="vertical" style={{ width: '100%' }} size="small">
            <Input.TextArea
              rows={2}
              placeholder="What should the video be about? e.g. Summarize the novel Pride and Prejudice in 5 minutes"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <Space wrap>
              <Select
                value={language}
                onChange={setLanguage}
                options={LANGUAGES}
                style={{ minWidth: 140 }}
              />
              <Select
                value={targetSeconds}
                onChange={setTargetSeconds}
                options={LENGTHS}
                style={{ minWidth: 140 }}
              />
              <Button
                type="primary"
                loading={create.isPending}
                disabled={topic.trim().length < 3}
                onClick={() => create.mutate()}
              >
                Generate video
              </Button>
            </Space>
            <Typography.Text type="secondary">
              The agent writes a narrated script, voices it, generates a scene image per beat, and
              assembles a subtitled video. Needs a chat, image and TTS key configured in AI
              settings.
            </Typography.Text>
          </Space>
        </Card>
      ) : null}

      {query.isLoading ? (
        <Spin />
      ) : videos.length === 0 ? (
        <Empty description="No videos yet" />
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {videos.map((v) => (
            <Card
              key={v.id}
              size="small"
              title={
                <Space wrap>
                  <span>{v.title || v.topic}</span>
                  <Tag color={STATUS_COLOR[v.status]}>{STATUS_LABEL[v.status]}</Tag>
                  <Tag>{v.language}</Tag>
                  <CostTag cost={v.cost} />
                  {isDripping(v) ? <Tag color="processing">Publishing…</Tag> : null}
                </Space>
              }
              extra={
                canEdit ? (
                  <Popconfirm
                    title="Delete this video?"
                    description="Removes the video and its files. Anything already published stays on the platform."
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => remove.mutate(v.id)}
                  >
                    <Button
                      danger
                      size="small"
                      loading={remove.isPending && remove.variables === v.id}
                    >
                      Delete
                    </Button>
                  </Popconfirm>
                ) : null
              }
            >
              {v.status === 'ready' && v.videoUrl ? (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 20,
                      alignItems: 'flex-start',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <video
                        src={v.videoUrl}
                        controls
                        preload="metadata"
                        style={{
                          width: 300,
                          maxWidth: '100%',
                          aspectRatio: '9 / 16',
                          objectFit: 'contain',
                          background: '#000',
                          borderRadius: 14,
                          boxShadow: '0 6px 20px rgba(0,0,0,0.22)',
                          display: 'block',
                        }}
                      />
                      <Button
                        icon={<DownloadOutlined />}
                        href={downloadUrl(v.videoUrl, `${slug(v.title || v.topic)}.mp4`)}
                        block
                      >
                        Download video
                      </Button>
                    </div>
                    {v.thumbnailUrl ? (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          width: 280,
                          maxWidth: '100%',
                        }}
                      >
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Thumbnail · 16:9
                        </Typography.Text>
                        <Image
                          src={v.thumbnailUrl}
                          alt="thumbnail"
                          style={{
                            width: '100%',
                            aspectRatio: '16 / 9',
                            objectFit: 'cover',
                            borderRadius: 12,
                            display: 'block',
                          }}
                        />
                        <Button
                          icon={<DownloadOutlined />}
                          href={downloadUrl(
                            v.thumbnailUrl,
                            `${slug(v.title || v.topic)}-thumbnail.png`,
                          )}
                          block
                        >
                          Download thumbnail
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  {v.description || v.hashtags.length ? (
                    <div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Caption (tap to copy)
                      </Typography.Text>
                      <Typography.Paragraph
                        copyable={{
                          text: [v.description, v.hashtags.map((h) => `#${h}`).join(' ')]
                            .filter(Boolean)
                            .join('\n\n'),
                        }}
                        style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
                      >
                        {v.description}
                        {v.hashtags.length ? (
                          <>
                            <br />
                            <br />
                            <Typography.Text type="secondary">
                              {v.hashtags.map((h) => `#${h}`).join(' ')}
                            </Typography.Text>
                          </>
                        ) : null}
                      </Typography.Paragraph>
                    </div>
                  ) : null}
                  <Divider style={{ margin: '4px 0' }} />
                  {canEdit ? (
                    <Space wrap>
                      <Button
                        type="primary"
                        loading={publishAll.isPending && publishAll.variables?.id === v.id}
                        onClick={() =>
                          publishAll.mutate({ id: v.id, intervalMinutes: pubInterval })
                        }
                      >
                        Publish everything{v.parts.length > 0 ? ' (full + all parts)' : ''}
                      </Button>
                      <Select
                        value={pubInterval}
                        onChange={setPubInterval}
                        style={{ minWidth: 150 }}
                        options={[
                          { value: 0, label: 'All at once' },
                          { value: 10, label: 'Every 10 min' },
                          { value: 30, label: 'Every 30 min' },
                        ]}
                      />
                    </Space>
                  ) : null}
                  {canEdit ? (
                    <Space wrap>
                      <Typography.Text type="secondary">Full video →</Typography.Text>
                      {FULL_PLATFORMS.map((p) => {
                        const done = v.published.some((r) => r.platform === p.value);
                        return (
                          <Button
                            key={p.value}
                            size="small"
                            loading={
                              publish.isPending &&
                              publish.variables?.id === v.id &&
                              publish.variables.partIndex === undefined &&
                              publish.variables.platform === p.value
                            }
                            disabled={done}
                            onClick={() => publish.mutate({ id: v.id, platform: p.value })}
                          >
                            {done ? `${p.label} ✓` : p.label}
                          </Button>
                        );
                      })}
                    </Space>
                  ) : null}
                  {v.parts.length > 0 ? (
                    <>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {v.parts.length} short-form parts (~60s) for TikTok / Shorts
                      </Typography.Text>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {v.parts.map((part) => (
                          <Space key={part.index} align="center" wrap>
                            <a href={part.videoUrl} target="_blank" rel="noreferrer">
                              <Tag color="geekblue">{part.label}</Tag>
                            </a>
                            <Button
                              size="small"
                              type="text"
                              icon={<DownloadOutlined />}
                              title="Download this part"
                              href={downloadUrl(
                                part.videoUrl,
                                `${slug(v.title || v.topic)}-part-${String(part.index + 1)}.mp4`,
                              )}
                            />
                            {canEdit
                              ? PART_PLATFORMS.map((p) => {
                                  const done = part.published.some((r) => r.platform === p.value);
                                  return (
                                    <Button
                                      key={p.value}
                                      size="small"
                                      loading={
                                        publish.isPending &&
                                        publish.variables?.id === v.id &&
                                        publish.variables.partIndex === part.index &&
                                        publish.variables.platform === p.value
                                      }
                                      disabled={done}
                                      onClick={() =>
                                        publish.mutate({
                                          id: v.id,
                                          platform: p.value,
                                          partIndex: part.index,
                                        })
                                      }
                                    >
                                      {done ? `${p.label} ✓` : p.label}
                                    </Button>
                                  );
                                })
                              : null}
                          </Space>
                        ))}
                      </Space>
                    </>
                  ) : null}
                </Space>
              ) : v.status === 'failed' ? (
                <Space direction="vertical" size="small">
                  <Typography.Text type="danger">{v.error ?? 'Generation failed'}</Typography.Text>
                  {canEdit ? (
                    <Button
                      icon={<ReloadOutlined />}
                      loading={retry.isPending && retry.variables === v.id}
                      onClick={() => retry.mutate(v.id)}
                    >
                      Retry
                    </Button>
                  ) : null}
                </Space>
              ) : (
                <Space>
                  <Spin size="small" />
                  <Typography.Text type="secondary">
                    {STATUS_LABEL[v.status]}
                    {v.scenes.length > 0 ? ` · ${String(v.scenes.length)} scenes` : ''}…
                  </Typography.Text>
                </Space>
              )}
            </Card>
          ))}
        </Space>
      )}
    </Space>
  );
}
